import type { Router, RouteRecordRaw } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { useUserStore } from '@/store/modules/user';
import { usePermissionStore } from '@/store/modules/permission';
import { asyncRoutes } from './routes/async';
import { hasRoutePermission, findRouteByPath } from './config/permission-utils';
import { UserRole } from './config/roles';
import { i18n } from '@/locales';
import { openNoAccessPermissionModal } from '@/components/business-components/dialog';
import {
  constantRoutes
} from '@/router/routes/constant'
export const routerWhiteList = constantRoutes.map(item => {
  return item.path;
});
export function pathIsInRouterWhiteList(url: string): boolean {
  if (typeof url !== 'string') return false;

  let p = url;
  if (/^https?:\/\//i.test(url)) {
    try { p = new URL(url).pathname; } catch {}
  }
  const i = Math.min(
    ...[p.indexOf('?'), p.indexOf('#')].map(x => (x < 0 ? Infinity : x))
  );
  if (i !== Infinity) p = p.slice(0, i);

  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return routerWhiteList.includes(p);
}

function goLogin(next: any, to: any) {
  if (routerWhiteList.includes(to.path)) {
    next();
  } else {
    next({ path: '/login', query: { redirect: to.fullPath } });
  }
}

export function setupGuards(router: Router) {
  router.beforeEach(async (to, from, next) => {
    NProgress.start();

    const userStore = useUserStore();
    const permStore = usePermissionStore();

    const hasToken = !!userStore.token;
    if (pathIsInRouterWhiteList(to.path)) {
      next();
      return;
    }
    if (!hasToken) {
      userStore.handleTokenExpired()
      return;
    }

    if (!userStore.profile) {
      try {
        await userStore.getUserInfo();
      } catch (error) {
        console.error('获取用户信息失败:', error);
        NProgress.done();
        userStore.handleTokenExpired()
        return;
      }
    }

    if (!permStore.isRoutesReady) {
      asyncRoutes.forEach(r => router.addRoute(r));
      permStore.generateRoutes(userStore.roleId as UserRole, asyncRoutes);
      permStore.isRoutesReady = true;
      return next({ ...to, replace: true });
    }

    if (!pathIsInRouterWhiteList(to.path)) {
      const roleId = userStore.roleId as UserRole;
      const matchedRoute = findRouteByPath(asyncRoutes, to.path);

      if (matchedRoute && !hasRoutePermission(roleId, matchedRoute)) {
        if (!userStore.hasAnyProductPermission) {
          next({ path: '/403', replace: true });
        } else {
          const modalConfig = {
            message: i18n.global.t('common.noAccessPermission'),
            confirmButtonText: i18n.global.t('common.ok'),
          };

          if (userStore.hasMultiProductPermission) {
            openNoAccessPermissionModal(modalConfig);
            if (to.path != '/switch') {
              next({ path: '/switch', replace: true });
            }
          } else {
            openNoAccessPermissionModal(modalConfig).then(() => {
              const urlMap: Record<number, string> = {
                1: import.meta.env.VITE_TALENT_SYSTEM_URL,
                2: import.meta.env.VITE_CAREER_SYSTEM_URL,
              };
              const productId = userStore.productIdsList[0];
              const targetUrl = urlMap[productId];
              targetUrl && (window.location.href = targetUrl);
            });
          }
        }
        NProgress.done();
        return;
      }
    }

    next();
  });

  router.afterEach((to) => {
    NProgress.done();

    // 设置浏览器标签 title
    if (to.meta?.title) {
      const title = i18n.global.t(to.meta.title as string);
      document.title = `${title} - Global Hub`;
    }
  });
}
